import type { PurchaseOrderResponse } from '@palaccount/contracts';
import { PackageCheck, Pencil, Receipt, Send, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { Amount } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { percentOf } from '@/lib/money';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause } from '@/lib/server-messages';
import { useCompanyToday } from '@/lib/session';

import {
  fetchPurchaseOrdersPage,
  useCancelPurchaseOrder,
  usePagedPurchaseOrders,
  usePurchaseOrder,
  usePurchaseOrderStats,
  useSendPurchaseOrder,
} from './api';
import { billedTone, canSend } from './po-rules';
import { purchaseOrderSteps } from './purchase-steppers';
import { PurchasesTabs } from './purchases-tabs';

// GEN-67 — the list is filterable by EVERY status the record carries.
const STATUSES: readonly PurchaseOrderResponse['status'][] = [
  'draft',
  'approved',
  'sent',
  'partially_received',
  'received',
  'closed',
  'cancelled',
];
/**
 * PUR-01 §7's ladder through the one palette: Draft grey, Approved and Sent
 * blue (active), Partially received amber (waiting on the rest), Received
 * green, Closed green-faded, Cancelled red-faded (BR-PUR-01-06).
 */
const TONES = {
  draft: 'draft',
  approved: 'posted',
  sent: 'posted',
  partially_received: 'overdue',
  received: 'completed',
  closed: 'closed',
  cancelled: 'cancelled',
} as const;

type PurchaseOrderStats = ReturnType<typeof usePurchaseOrderStats>['data'] & object;

/**
 * PUR-01's list on the shared list-and-detail page (wave 3B). The selected
 * order sits at Purchase order in the purchasing chain, the receipt and the
 * bill being the stepper's acts while the order is sent (D-09), and says how
 * much of it has arrived and been billed — on the two bases wave 1C made
 * explicit: received against the order EXCLUDING VAT (`orderedNet`), billed
 * against the grand total, the over-billed word beside an amber figure
 * (NFR-39). The acts: send (the commitment door, with the expected date it
 * now demands — wave 1C), raise the receipt, raise the bill, print, cancel
 * (GEN-59, reason stored).
 */
export function OrdersListPage() {
  const { t, i18n } = useTranslation();
  const today = useCompanyToday();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const sendOrder = useSendPurchaseOrder(selectedId);
  const cancelOrder = useCancelPurchaseOrder(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('purchasing.edit');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (status: PurchaseOrderResponse['status']) => t(`po.status.${status}`);
  const chip = (order: PurchaseOrderResponse) => <StatusChip status={order.status} tones={TONES} ns="po" />;
  const exportColumns = [
    { header: t('po.colNo'), value: (o: PurchaseOrderResponse) => o.code },
    { header: t('common.date'), value: (o: PurchaseOrderResponse) => o.orderDate, kind: 'date' as const },
    { header: t('po.fExpectedDate'), value: (o: PurchaseOrderResponse) => o.expectedDate ?? '', kind: 'date' as const },
    { header: t('po.colSupplier'), value: (o: PurchaseOrderResponse) => o.partnerName },
    { header: t('sales.colStatus'), value: (o: PurchaseOrderResponse) => statusLabel(o.status) },
    { header: t('sales.exportColCurrency'), value: (o: PurchaseOrderResponse) => o.currency },
    { header: t('sales.colGrandTotal'), value: (o: PurchaseOrderResponse) => o.grandTotal, kind: 'number' as const },
    { header: t('po.colOrderedNet'), value: (o: PurchaseOrderResponse) => o.orderedNet, kind: 'number' as const },
    { header: t('po.colBilled'), value: (o: PurchaseOrderResponse) => o.billedTotal, kind: 'number' as const },
  ];
  const pdfRequest = (order: PurchaseOrderResponse) => ({ kind: 'purchase_order' as const, locale, subjectId: order.id });
  const toFilters = (filters: { status: PurchaseOrderResponse['status'] | ''; q: string; dateFrom: string; dateTo: string; partnerId: string }) => ({
    status: filters.status || undefined,
    q: filters.q || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    partnerId: filters.partnerId || undefined,
  });
  const awaiting = (order: PurchaseOrderResponse) =>
    order.status === 'sent' || order.status === 'approved' || order.status === 'partially_received';

  const config: DocumentListConfig<PurchaseOrderResponse, PurchaseOrderResponse['status'], PurchaseOrderStats> = {
    titleKey: 'po.title',
    tabs: <PurchasesTabs />,
    newAction: { to: '/purchases/orders/new', labelKey: 'common.new', permission: 'purchasing.create', reasonKey: 'perm.cannot.createPurchaseOrders' },
    statuses: STATUSES,
    statusLabel,
    /* GEN-50: the contract takes `q` (the printed code or the supplier's name). */
    search: { labelKey: 'doclist.searchLabel', placeholderKey: 'doclist.searchPlaceholder' },
    dateRange: true,
    partner: { kind: 'supplier' },
    useList: (filters, page, pageSize) => {
      const query = usePagedPurchaseOrders(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'orders'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => usePurchaseOrder(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = usePurchaseOrderStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'open', labelKey: 'po.tileOpen', format: 'count', value: (s) => s.openOrders },
        { key: 'openValue', labelKey: 'po.tileOpenValue', format: 'moneyWhole', value: (s) => s.openValue, currency: (s) => s.currency },
        { key: 'awaiting', labelKey: 'po.tileAwaitingReceipt', format: 'count', value: (s) => s.awaitingReceipt, tone: 'warn' },
        { key: 'notBilled', labelKey: 'po.tileReceivedNotBilled', format: 'count', value: (s) => s.receivedNotBilled },
      ],
    },
    card: (order) => {
      // EC-19-40 — a delivery past its expected date must not look like an on-time one.
      const late = awaiting(order) && order.expectedDate !== null && order.expectedDate < today;
      return {
        code: order.code,
        chip: chip(order),
        title: order.partnerName,
        meta: (
          <>
            <bdi dir="ltr">{order.orderDate}</bdi>
            {order.expectedDate ? (
              <span className={late ? 'ms-2 text-amber-700 dark:text-amber-400' : 'ms-2'}>
                {t('po.fExpectedDate')} <bdi dir="ltr">{order.expectedDate}</bdi>
                {late ? <span className="ms-1 text-xs font-semibold">{t('po.lateChip')}</span> : null}
              </span>
            ) : null}
          </>
        ),
        amount: { value: order.grandTotal, currency: order.currency },
      };
    },
    columns: { titleKey: 'po.colSupplier', metaKey: 'common.date', amountKey: 'sales.colGrandTotal', statusKey: 'sales.colStatus' },
    emptyKey: 'po.empty',
    selectOneKey: 'po.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'purchase-orders',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchPurchaseOrdersPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows) => (
      <>
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="purchase-orders" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      subline: (order) => (
        <>
          {order.partnerName} · {t('po.fExpectedDate')} <bdi dir="ltr">{order.expectedDate ?? '—'}</bdi>
        </>
      ),
      steps: { labelKey: 'po.stepper', of: purchaseOrderSteps },
      /* "% received · % billed" — received on the net basis, billed on the gross; over-billed named in words. */
      progress: (order) => {
        const received = percentOf(order.receivedTotal, order.orderedNet);
        const billed = percentOf(order.billedTotal, order.grandTotal);
        return billedTone(order) === 'over'
          ? t('po.listProgressOver', { received, billed })
          : t('po.listProgress', { received, billed });
      },
      actions: (order) => (
        <>
          <Button asChild variant="outline">
            <Link to={`/purchases/orders/${order.id}`}>
              <Pencil aria-hidden /> {order.status === 'draft' ? t('common.edit') : t('doclist.open')}
            </Link>
          </Button>
          {order.status === 'draft' || order.status === 'approved' ? (
            <Gated permission="purchasing.edit" reasonKey="perm.cannot.sendPurchaseOrders">
              <Button
                onClick={() => void act(() => sendOrder.mutateAsync())}
                disabled={busy || !canSend(order)}
                title={canSend(order) ? undefined : t('po.sendNeedsDate')}
              >
                <Send aria-hidden /> {t('po.actSend')}
              </Button>
            </Gated>
          ) : null}
          {order.status === 'sent' || order.status === 'partially_received' ? (
            <Gated permission="purchasing.create" reasonKey="perm.cannot.createReceipts">
              <Button asChild variant="outline">
                <Link to={`/purchases/receipts/new?poId=${order.id}`}>
                  <PackageCheck aria-hidden /> {t('po.actReceipt')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {order.status === 'sent' || order.status === 'partially_received' || order.status === 'received' ? (
            <Gated permission="purchasing.create" reasonKey="perm.cannot.createBills">
              <Button asChild variant="outline">
                <Link to={`/purchases/bills/new?poId=${order.id}`}>
                  <Receipt aria-hidden /> {t('po.actBill')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          <DocumentPdfButton request={pdfRequest(order)} />
          {order.status !== 'cancelled' && order.status !== 'closed' && order.status !== 'received' ? (
            <Gated permission="purchasing.edit" reasonKey="perm.cannot.cancelPurchaseOrders">
              <Button variant="destructive" onClick={() => setCancelAsk(true)} disabled={busy}>
                <XCircle aria-hidden /> {t('po.actCancel')}
              </Button>
            </Gated>
          ) : null}
          {order.status !== 'cancelled' && order.status !== 'closed' && order.status !== 'received' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('po.listCancelTitle')}
              prompt={t('po.listCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => {
                setCancelAsk(false);
                void act(() => cancelOrder.mutateAsync(reason));
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </>
      ),
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'qty', labelKey: 'sales.colQty', align: 'end', className: 'w-24' },
          { key: 'unitPrice', labelKey: 'sales.colUnitPrice', align: 'end', className: 'w-32' },
          { key: 'total', labelKey: 'sales.colTotal', align: 'end', className: 'w-32' },
        ],
        rows: (order) =>
          order.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <bdi key="q" dir="ltr" className="font-mono">{line.qty.replace(/\.?0+$/, '')}</bdi>,
              <Amount key="u" value={line.unitPrice} currency={order.currency} />,
              <Amount key="t" value={line.lineTotal} currency={order.currency} />,
            ],
          })),
      },
      totals: (order) => ({
        labelKey: 'orders.netTaxTotal',
        amounts: [
          { value: order.subtotal, currency: order.currency },
          { value: order.taxAmount, currency: order.currency },
          { value: order.grandTotal, currency: order.currency, emphasis: true },
        ],
      }),
    },
  };

  return <DocumentListPage config={config} />;
}
